import React, { useEffect, useState, useContext } from "react";
import axios from "../../axiosConfig";
import "../../styles/AssignExerciseModal.css";
import { TrainerContext } from "../../context/TrainerContext";
import { IoSearchOutline } from 'react-icons/io5';
import { useToast } from "../commons/toast/ToastContext";

const AssignExerciseModal = ({ isOpen, onClose, onSave, dogId }) => {
    const { trainer } = useContext(TrainerContext);
    const toast = useToast();
    const [exercises, setExercises] = useState([]);
    const [filteredExercises, setFilteredExercises] = useState([]);
    const [selectedExercises, setSelectedExercises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (!trainer?.schoolId || !isOpen) return;

        const fetchExercises = async () => {
            try {
                const token = localStorage.getItem("jwt");
                const response = await axios.get(`/api/v1/exercise/school/${trainer.schoolId}/dog/${dogId}/unassigned`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const exercisesData = response.data || [];
                setExercises(exercisesData);
                setFilteredExercises(exercisesData);
                setError("");
            } catch {
                setError("Nie udało się pobrać ćwiczeń.");
            } finally {
                setLoading(false);
            }
        };

        fetchExercises();
    }, [trainer, dogId, isOpen]);

    useEffect(() => {
        const filtered = exercises.filter((exercise) =>
            exercise.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredExercises(filtered);
    }, [searchTerm, exercises]);

    const handleSelectExercise = (exerciseId) => {
        setSelectedExercises((prevSelected) =>
            prevSelected.includes(exerciseId)
                ? prevSelected.filter((id) => id !== exerciseId)
                : [...prevSelected, exerciseId]
        );
    };

    const handleSave = async () => {
        if (selectedExercises.length === 0) {
            setError("Wybierz co najmniej jedno ćwiczenie.");
            toast.warning("Wybierz co najmniej jedno ćwiczenie", "Brak wyboru");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");
            const requests = selectedExercises.map((exerciseId) => ({
                exerciseId,
                assignedDate: new Date().toISOString().split("T")[0],
            }));

            await axios.post(`/api/v1/exercise/dog/${dogId}/assign`, requests, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setSelectedExercises([]);
            setSearchTerm("");
            setError("");
            toast.success(
                selectedExercises.length === 1
                    ? 'Ćwiczenie zostało przypisane'
                    : `Przypisano ${selectedExercises.length} ćwiczeń`,
                'Sukces'
            );
            onSave();
            onClose();
        } catch {
            setError("Nie udało się przypisać ćwiczeń.");
            toast.error("Nie udało się przypisać ćwiczeń", "Błąd");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="assign-exercise-modal__overlay">
            <div className="assign-exercise-modal__content">
                <div className="assign-exercise-modal__header">
                    <div className="assign-exercise-modal__header-info">
                        <h2 className="assign-exercise-modal__title">Przypisz ćwiczenie</h2>
                        <div className="assign-exercise-modal__selected-count">
                            {selectedExercises.length > 0 && (
                                <span className="assign-exercise-modal__selected-badge">
                                    Wybrano: {selectedExercises.length}
                                </span>
                            )}
                        </div>
                    </div>
                    <button className="assign-exercise-modal__close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="assign-exercise-modal__body">
                    {/* Search Section with Icon */}
                    <div className="assign-exercise-modal__search-section">
                        <IoSearchOutline className="assign-exercise-modal__search-icon" />
                        <input
                            type="text"
                            className="assign-exercise-modal__search-bar"
                            placeholder="Wpisz nazwę ćwiczenia..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Error Message */}
                    {error && <div className="assign-exercise-modal__error">{error}</div>}

                    {/* Exercises Section */}
                    <div className="assign-exercise-modal__exercises-section">
                        <div className="assign-exercise-modal__exercises-header">
                            <span className="assign-exercise-modal__exercises-count">
                                {filteredExercises.length} dostępne ćwiczenia
                            </span>
                        </div>

                        {loading ? (
                            <div className="assign-exercise-modal__loading">
                                Ładowanie ćwiczeń...
                            </div>
                        ) : (
                            <div className="assign-exercise-modal__exercises-list">
                                {filteredExercises.length === 0 ? (
                                    <div className="assign-exercise-modal__no-exercises">
                                        {searchTerm ? (
                                            <>
                                                <div className="assign-exercise-modal__no-exercises-icon">🔍</div>
                                                <h3>Brak wyników</h3>
                                                <p>Nie znaleziono ćwiczeń dla "{searchTerm}"</p>
                                            </>
                                        ) : (
                                            <>
                                                <div className="assign-exercise-modal__no-exercises-icon">📋</div>
                                                <h3>Brak dostępnych ćwiczeń</h3>
                                                <p>Wszystkie ćwiczenia zostały już przypisane do tego psa.</p>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    filteredExercises.map((exercise) => (
                                        <label
                                            key={exercise.exerciseId}
                                            className={`assign-exercise-modal__exercise-option ${
                                                selectedExercises.includes(exercise.exerciseId)
                                                    ? 'assign-exercise-modal__exercise-option--selected'
                                                    : ''
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedExercises.includes(exercise.exerciseId)}
                                                onChange={() => handleSelectExercise(exercise.exerciseId)}
                                                className="assign-exercise-modal__exercise-checkbox"
                                            />
                                            <div className="assign-exercise-modal__exercise-info">
                                                <div className="assign-exercise-modal__exercise-name">
                                                    {exercise.name}
                                                </div>
                                                <div className="assign-exercise-modal__exercise-difficulty">
                                                    Poziom: {exercise.difficultyLevel}
                                                </div>
                                            </div>
                                        </label>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="assign-exercise-modal__footer">
                    <button className="assign-exercise-modal__cancel-btn" onClick={onClose}>
                        Anuluj
                    </button>
                    <button
                        className="assign-exercise-modal__save-btn"
                        onClick={handleSave}
                        disabled={selectedExercises.length === 0 || loading}
                    >
                        Przypisz
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignExerciseModal;